import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { Order } from '../../../../types/order/order';
import { StockLocation } from '../../../../types/stock_location/stockLocation';
import { BaseFilter } from '../common/BaseFilter';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { User } from '../../../../types/user/user';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { DateTimeFilter } from '../DateTimeFilter';
import { StockLocationFilter } from '../StockLocationFilter';
import { OrderStatusFilter } from './OrderStatusFilter';
import { OrderTypeFilter } from './OrderTypeFilter';
import { TagFilter } from '../TagFilter';
import { toFilterString } from '../../../../util/string.util';
import { orderTableFilter } from '../../../../ui/pages/Common/OrderTable';
import { t } from '../../../../types/translation/Translator';
import { CreatedByFilter } from '../CreatedByFilter/CreatedByFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { EntityType } from '../../../../types/comment';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { Company } from '../../../../types/company';
import { StockLocationFilterMode } from '../StockLocationFilter/StockLocationFilter';

export class OrderFilter extends BaseFilter<Order> {
  stockLocations: Map<string, StockLocation>;
  tableSettings: TableSettings<any>;
  tagRelations: Map<string, TagRelation>;
  users: Map<string, User>;
  customFields: Map<string, CustomField>;
  orders: Map<string, Order>;
  company: Company;

  constructor(
    stockLocations: Map<string, StockLocation>,
    tableSettings: TableSettings<any>,
    tagRelations: Map<string, TagRelation>,
    users: Map<string, User>,
    customFields: Map<string, CustomField>,
    orders: Map<string, Order>,
    company: Company,
    key?: string,
  ) {
    super(FilterStorageKey.ORDER, key);
    this.stockLocations = stockLocations;
    this.tableSettings = tableSettings;
    this.tagRelations = tagRelations;
    this.users = users;
    this.customFields = customFields;
    this.orders = orders;
    this.company = company;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new DateTimeFilter(),
    new StockLocationFilter(this.tagRelations, this.company, StockLocationFilterMode.source),
    new StockLocationFilter(this.tagRelations, this.company, StockLocationFilterMode.destination),
    new OrderStatusFilter(this.stockLocations, this.tableSettings),
    new OrderTypeFilter(this.stockLocations, this.tableSettings),
    new TagFilter(EntityType.stockLocation),
    new CreatedByFilter(this.users),
    ...CustomFieldFilter.for(CustomFieldEntityType.order, this.orders, this.customFields),
  ];

  toLabel(): string {
    return t(TranslationKey.order).singular.label;
  }

  toInnerContent(): string | JSX.Element {
    return <></>;
  }

  toDropdownContent(): React.JSX.Element {
    return <></>;
  }

  toIcon(): React.JSX.Element {
    return <></>;
  }

  toCount(): number {
    return 0;
  }

  getTableSettings(item: Order) {
    return this.tableSettings;
  }

  search(item: Order, filter: string) {
    return orderTableFilter(item, toFilterString(filter), this.stockLocations, this.users, this.getTableSettings(item));
  }

  filteredItems(items: Order[], query: string, filterProps: FilterProps): Order[] {
    return items
      .filter(item => {
        return [
          DateTimeFilter.filter(filterProps, new Date(item.createdAt)),
          TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.fromStockLocationId),
          TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.toStockLocationId),
          StockLocationFilter.filter(
            filterProps,
            item.fromStockLocationId ? this.stockLocations.get(item.fromStockLocationId) : undefined,
            StockLocationFilterMode.source,
          ),
          StockLocationFilter.filter(
            filterProps,
            item.toStockLocationId ? this.stockLocations.get(item.toStockLocationId) : undefined,
            StockLocationFilterMode.destination,
          ),
          OrderFilter.filter(filterProps, item, this.customFields),
          this.search(item, query),
        ].every(value => value === null || value);
      })
      .sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());
  }

  static filter(filterProps: FilterProps, item?: Order, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.orderType?.size && !filterProps.orderType.has(item.type)) return false;
    // if (item && filterProps.orderStatus?.size && !filterProps.orderStatus.has(item.status)) return false;
    if (item && filterProps.user?.size && !filterProps.user.has(item.createdBy)) return false;
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.order);
  }
}
